import { configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { bannerReducer } from '@ths/shared/store/slices/banner';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { Middleware, combineReducers } from 'redux';
import logger from 'redux-logger';

import { bookingReducer } from './bookingSlice';
import { clientConfigReducer } from './clientConfigSlice';
import { enumsReducer } from './enumsSlice';
import { groupDetailsReducer } from './groupDetailsSlice';
import { unknownGuestReducer } from './unknownGuestSlice';
import { userReducer } from './userSlice';

const middleware: Middleware[] = [];

if (import.meta.env.MODE === 'development') {
  middleware.push(logger);
}

const rootReducer = combineReducers({
  banner: bannerReducer,
  user: userReducer,
  booking: bookingReducer,
  clientConfig: clientConfigReducer,
  groupDetails: groupDetailsReducer,
  enums: enumsReducer,
  unknownGuest: unknownGuestReducer,
});

export type AppState = ReturnType<typeof rootReducer>;

// Create Sentry Redux enhancer with PII sanitization
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Sanitize Redux state before sending to Sentry
  stateTransformer: (state: AppState) => {
    return {
      ...state,
      user: state.user
        ? {
            id: state.user.id,
            roles: state.user.roles,
            // Redact PII fields
            email: 'REDACTED',
            username: 'REDACTED',
            picture: 'REDACTED',
            profile: 'REDACTED',
          }
        : state.user,
      // Booking state contains location and dates but no PII
      // Keep it as-is for debugging context
    };
  },
  // Sanitize Redux actions before sending to Sentry
  actionTransformer: (action) => {
    // Redact PII from actions that set user data
    if (action.type.includes('setUser') && action.payload) {
      return {
        ...action,
        payload: {
          id: action.payload.id,
          roles: action.payload.roles,
          // Redact PII fields
          email: 'REDACTED',
          username: 'REDACTED',
          picture: 'REDACTED',
          profile: 'REDACTED',
        },
      };
    }
    return action;
  },
});

export const createAppStore = () =>
  configureStore({
    reducer: rootReducer,
    devTools: true,
    // @ts-expect-error TODO fix middleware types
    middleware: (getDefaultMiddleware) => [
      ...getDefaultMiddleware(),
      ...middleware,
    ],
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers().concat(sentryReduxEnhancer),
  });

export const store = createAppStore();

export type AppDispatch = typeof store.dispatch;

// use these typed hooks instead of Redux useSelector and useDispatch hooks
export const useAppSelector: TypedUseSelectorHook<AppState> = useSelector;
export const useAppDispatch = () => useDispatch<AppDispatch>();
